import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexto/AuthContexto';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Search, Plus, Pencil, Trash2, Users, Car, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

const camposVacios = { nombre_completo: '', telefono: '', email: '', direccion: '', notas: '' };

export default function Clientes() {
  const { usuario } = useAuth();
  const [codigoPais, setCodigoPais] = useState('+52');
  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(camposVacios);
  const [guardando, setGuardando] = useState(false);
  const [eliminarId, setEliminarId] = useState(null);
  const navigate = useNavigate();

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/clientes', { params: { busqueda } });
      setClientes(res.data);
    } catch { toast.error('Error al cargar clientes'); }
    finally { setCargando(false); }
  }, [busqueda]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    // Get country code from tenant config
    api.get('/pagos/pasarela-pago').then(r => {
      const pais = r.data?.pais || 'MX';
      const codigos = { MX: '+52', CO: '+57', AR: '+54', CL: '+56', PE: '+51', EC: '+593', VE: '+58', GT: '+502', CU: '+53', DO: '+1', HN: '+504', SV: '+503', NI: '+505', CR: '+506', PA: '+507', PY: '+595', UY: '+598', BO: '+591', PR: '+1', GQ: '+240', ES: '+34' };
      setCodigoPais(codigos[pais] || '+52');
    }).catch(() => {});
  }, []);

  const abrir = (cliente = null) => {
    setEditando(cliente);
    setForm(cliente ? { nombre_completo: cliente.nombre_completo, telefono: cliente.telefono, email: cliente.email, direccion: cliente.direccion, notas: cliente.notas || '' } : camposVacios);
    setDialog(true);
  };

  const guardar = async () => {
    if (!form.nombre_completo.trim()) { toast.error('El nombre es obligatorio'); return; }
    setGuardando(true);
    try {
      const datos = { ...form };
      // Auto-prepend country code to phone if not already present
      if (datos.telefono) {
        const tel = datos.telefono.replace(/\D/g, '');
        const codigo = codigoPais.replace('+', '');
        datos.telefono = tel.startsWith(codigo) ? tel : codigo + tel;
      }
      if (editando) { await api.put(`/clientes/${editando.id}`, datos); toast.success('Cliente actualizado'); }
      else { await api.post('/clientes', datos); toast.success('Cliente creado'); }
      setDialog(false);
      cargar();
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const eliminar = async () => {
    try {
      await api.delete(`/clientes/${eliminarId}`);
      toast.success('Cliente eliminado');
      setEliminarId(null);
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="clientes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50">Clientes</h1>
        <Button onClick={() => abrir()} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="nuevo-cliente-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Cliente
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input placeholder="Buscar clientes..." value={busqueda} onChange={e => setBusqueda(e.target.value)}
          className="pl-10 bg-slate-900 border-slate-800 text-slate-200 placeholder:text-slate-600" data-testid="buscar-clientes-input" />
      </div>

      {cargando ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 bg-slate-800" />)}</div>
      ) : clientes.length === 0 ? (
        <div className="text-center py-16" data-testid="clientes-empty">
          <Users className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">No hay clientes registrados</p>
          <Button onClick={() => abrir()} variant="ghost" className="mt-3 text-amber-500 hover:text-amber-400">
            Agregar primer cliente
          </Button>
        </div>
      ) : (
        <div className="bg-slate-900/40 border border-slate-800 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Nombre</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden md:table-cell">Telefono</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden lg:table-cell">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Vehiculos</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-center">Ordenes</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clientes.map(c => (
                <TableRow key={c.id} className="border-slate-800/50 hover:bg-slate-800/40 cursor-pointer"
                  onClick={() => navigate(`/clientes/${c.id}`)} data-testid={`cliente-row-${c.id}`}>
                  <TableCell className="text-sm text-slate-200 font-medium">{c.nombre_completo}</TableCell>
                  <TableCell className="text-sm text-slate-400 hidden md:table-cell">{c.telefono}</TableCell>
                  <TableCell className="text-sm text-slate-400 hidden lg:table-cell">{c.email}</TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                      <Car className="w-3 h-3" /> {c.total_vehiculos || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                      <ClipboardList className="w-3 h-3" /> {c.total_ordenes || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => abrir(c)} className="text-slate-400 hover:text-white h-8 w-8" data-testid={`editar-cliente-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setEliminarId(c.id)} className="text-slate-400 hover:text-red-400 h-8 w-8" data-testid={`eliminar-cliente-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="cliente-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-slate-100">
              {editando ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nombre completo *</Label>
              <Input value={form.nombre_completo} onChange={e => setForm({...form, nombre_completo: e.target.value})}
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cliente-nombre-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Telefono <span className="text-amber-500">({codigoPais})</span></Label>
                <div className="flex gap-1">
                  <span className="inline-flex items-center px-2 bg-slate-800 border border-slate-700 rounded-sm text-sm text-amber-400 font-mono shrink-0">{codigoPais}</span>
                  <Input value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value.replace(/\D/g, '')})}
                    placeholder="10 digitos" maxLength={10}
                    className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="cliente-telefono-input" />
                </div></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
                <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cliente-email-input" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Direccion</Label>
              <Input value={form.direccion} onChange={e => setForm({...form, direccion: e.target.value})}
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="cliente-direccion-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Notas</Label>
              <Textarea value={form.notas} onChange={e => setForm({...form, notas: e.target.value})}
                className="bg-slate-950 border-slate-700 text-slate-200" rows={2} data-testid="cliente-notas-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardar} disabled={guardando} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="guardar-cliente-btn">
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!eliminarId} onOpenChange={() => setEliminarId(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Eliminar cliente</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esta accion no se puede deshacer. El cliente sera eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirmar-eliminar-btn">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
